import React, { createContext, useContext, useMemo, ReactNode, useState, useCallback } from 'react';

interface BaseFilterValues {
  [filterKey: string]: {
    selectedValues: string[];
    isAllSelected: boolean;
  };
}

interface HubspotFiltersContextValue {
  filterValues: BaseFilterValues;
  setFilterValue: (filterKey: string, selectedValues: string[]) => void;
  clearAllFilterValues: () => void;
}

interface HubspotFiltersProviderProps {
  children: ReactNode;
}

const HubspotFiltersContext = createContext<HubspotFiltersContextValue | undefined>(undefined);

export const HubspotFiltersProvider: React.FC<HubspotFiltersProviderProps> = ({ children }) => {
  // Filter values state management only
  const [filterValues, setFilterValues] = useState<BaseFilterValues>({});

  const setFilterValue = useCallback((filterKey: string, selectedValues: string[]) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterKey]: {
        selectedValues,
        isAllSelected: selectedValues.length === 0, // Consider empty selection as "all selected"
      },
    }));
  }, []);

  const clearAllFilterValues = useCallback(() => {
    setFilterValues({});
  }, []);

  const value: HubspotFiltersContextValue = useMemo(
    () => ({
      filterValues,
      setFilterValue,
      clearAllFilterValues,
    }),
    [filterValues, setFilterValue, clearAllFilterValues],
  );
  return <HubspotFiltersContext.Provider value={value}>{children}</HubspotFiltersContext.Provider>;
};

/**
 * Hook to access HubspotFilters context (only for filter values state)
 */
export const useHubspotFiltersContext = () => {
  const context = useContext(HubspotFiltersContext);
  if (context === undefined) {
    throw new Error('useHubspotFiltersContext must be used within a HubspotFiltersProvider');
  }
  return context;
};
